import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { config } from '../../utils/config';

interface AccountData {
  credits_balance: number;
}

const SimpleAccountDashboard: React.FC = () => {
  const { user, session } = useAuth();
  const [accountData, setAccountData] = useState<AccountData | null>(null);
  const [loading, setLoading] = useState(true);

  // Load cached data on mount
  useEffect(() => {
    const cachedCredits = localStorage.getItem('zoningpal_credits');
    if (cachedCredits) {
      setAccountData({ credits_balance: parseInt(cachedCredits) });
    }
  }, []);

  // Load account data
  const loadAccountData = async () => {
    if (!user || !session) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${config.API_URL}/api/payments-simple/account`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // Don't show error for account data, just use default values
        console.warn('Account data not available, using defaults');
        setAccountData({
          credits_balance: 0
        });
        return;
      }

      const data = await response.json();
      setAccountData(data.account);
      
      // Cache the credits
      if (data.account?.credits_balance !== undefined) {
        localStorage.setItem('zoningpal_credits', data.account.credits_balance.toString());
      }
    } catch (err: any) {
      console.warn('Error loading account data, using defaults:', err);
      // Don't set error for account data, just use default values
      setAccountData({
        credits_balance: 0
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccountData();
  }, [user, session]);


  if (loading) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
      }}>
        <div style={{ 
          fontSize: '14px', 
          color: '#64748b' 
        }}>
          Loading account information...
        </div>
      </div>
    );
  }


  return (
    <div style={{ 
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      background: 'white',
      position: 'relative'
    }}>
      {/* Welcome Header */}
      <div style={{ 
        textAlign: 'center',
        padding: '32px 32px 40px',
        borderBottom: '1px solid #f1f5f9'
      }}>
        <h2 style={{ 
          fontSize: '20px', 
          fontWeight: '600', 
          color: '#1e293b',
          margin: '0 0 4px 0'
        }}>
          Welcome, {user?.email}
        </h2>
        <p style={{ 
          fontSize: '14px', 
          color: '#64748b',
          margin: '0'
        }}>
          Manage your credits and generate reports
        </p>
      </div>

      {/* Credits Balance - Centered */}
      <div style={{ 
        padding: '40px 32px',
        textAlign: 'center'
      }}>
        <h3 style={{ 
          fontSize: '12px', 
          fontWeight: '600', 
          color: '#64748b',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          margin: '0 0 16px 0'
        }}>
          Credits Balance
        </h3>
        <div style={{ 
          fontSize: '48px', 
          fontWeight: '700', 
          color: '#1e293b',
          margin: '0 0 8px 0'
        }}>
          {accountData?.credits_balance || 0}
        </div>
        <div style={{ 
          fontSize: '16px', 
          color: '#64748b',
          margin: '0 0 24px 0'
        }}>
          Each analysis costs 1 credit
        </div>
        {(!accountData?.credits_balance || accountData.credits_balance === 0) && (
          <div style={{ 
            padding: '16px 20px',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#475569',
            margin: '0 auto',
            maxWidth: '400px',
            fontWeight: '400'
          }}>
            You don't have any credits. Purchase credits to start generating zoning analysis reports.
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleAccountDashboard;